import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FtuCsvUpload } from './components/ftu-csv-upload/ftu-csv-upload';
import { useFtuCsvCallbacks } from './ftu-csv-import-flow';
import styles from './ftu-order-config-step-body.module.less';
import { messages } from './messages';
import { ImportType } from '../../../../../../components/csv-import/data/types/import-type';
import { useImportContext } from '../../../../../../components/csv-import/ui/components/import-context';
import { useOrderCSVImportContext } from '../../../../../../components/order-import/order-import-context';
import { ShipStationAppId } from '../../../../../../connection-components/constants';
import { useModal } from '../../../../../../hooks/use-modal';
import MarketplaceSetupModal from '../../../../../../pages/settings/stores/marketplace-connection-modal/setup';
import Globals from '../../../../../../store/global-selectors';
import { setStoreGuid } from '../../../../../../store/orders/import/orders.import.actions';
import { refreshStoreList } from '../../../../../../store/seller/seller.actions';
import { WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';

const createNewStoreId = 'create-new-store';

export const FtuOrderConfigStepBody = () => {
  const intl = useIntl();
  const { onCsvFileChange } = useFtuCsvCallbacks();
  const { trackProcess, initRequest, setFile } = useImportContext();
  const orderCSVContext = useOrderCSVImportContext() || { storeDropdownOptions: [] };
  const selectedStoreGuid = useSelector(Globals.selectOrdersImportSelectedStoreGuid);
  const latestImport = useSelector((state) => Globals.selectCsvImportType(state, ImportType.order));
  const dispatch = useDispatch();

  const storeDropdownList = useMemo(
    () => [
      {
        storeGuid: createNewStoreId,
        name: intl.formatMessage(messages.createNewManualStore),
      },
      ...(orderCSVContext.storeDropdownOptions ?? []),
    ],
    [orderCSVContext.storeDropdownOptions, intl],
  );

  const { open: openNewManualStoreModal, close: closeNewManualStoreModal, isOpen: isNewManualStoreModalOpen } = useModal();

  const onStoreSelected = useCallback(
    (storeGuid: string) => {
      trackProcess(WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY, {
        property: 'selectStore',
        value: storeGuid,
      });
      if (storeGuid === createNewStoreId) {
        openNewManualStoreModal();
      } else {
        dispatch(setStoreGuid(storeGuid));
      }
    },
    [openNewManualStoreModal, dispatch, trackProcess],
  );

  const onSaveNewStore = useCallback(
    (savedStore: { StoreGuid: string }) => {
      dispatch(refreshStoreList());
      closeNewManualStoreModal();
      dispatch(setStoreGuid(savedStore.StoreGuid));
    },
    [closeNewManualStoreModal, dispatch],
  );

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (file) {
        setFile(file);
        trackProcess(WorkflowSteps.CSV_IMPORT_UPLOAD_FILE, {
          file: {
            name: file.name,
            size: file.size,
          },
        });
        onCsvFileChange?.(true);
      } else {
        onCsvFileChange?.(false);
      }
    },
    [setFile, trackProcess, onCsvFileChange],
  );

  return (
    <div className={styles.configStepContainer}>
      <FtuCsvUpload
        file={initRequest.csvFile}
        importStatus={latestImport?.status}
        jobId={latestImport?.jobId}
        onFileChange={handleFileChange}
        onStoreSelected={onStoreSelected}
        selectedStoreGuid={selectedStoreGuid ?? ''}
        stores={storeDropdownList}
      />
      {isNewManualStoreModalOpen ? (
        <MarketplaceSetupModal
          marketplaceApiCode={ShipStationAppId}
          onBack={closeNewManualStoreModal}
          onClose={closeNewManualStoreModal}
          onSave={onSaveNewStore}
          showNotification={false}
          storeGuid={undefined}
        />
      ) : null}
    </div>
  );
};
